/**
 * ProductLookup Component
 * =======================
 * 
 * A reusable product search/lookup component with TWO separate input boxes.
 * Both Product Code and Description inputs trigger the search.
 * When a product is selected, BOTH fields are populated.
 * 
 * USAGE:
 * ------
 * import ProductLookup from '../components/ProductLookup';
 * 
 * <ProductLookup
 *   codeValue={item.product_code}
 *   descriptionValue={item.description}
 *   onSelect={(product) => handleSelectProduct(index, product)}
 *   onCodeChange={(value) => handleChange(index, 'product_code', value)}
 *   onDescriptionChange={(value) => handleChange(index, 'description', value)}
 *   showPrice={true}
 *   showStock={true}
 *   codeClassName="w-32"
 *   descriptionClassName="flex-1"
 * />
 * 
 * PROPS:
 * ------
 * - codeValue: string - Current product code value
 * - descriptionValue: string - Current description value
 * - onSelect: (product) => void - Callback when product is selected (populates both fields)
 * - onCodeChange: (value) => void - Callback for code input changes
 * - onDescriptionChange: (value) => void - Callback for description input changes
 * - codePlaceholder: string - Placeholder for code input
 * - descriptionPlaceholder: string - Placeholder for description input
 * - showPrice: boolean - Show unit price in results
 * - showStock: boolean - Show stock level in results
 * - showWeight: boolean - Show product weight in results
 * - codeClassName: string - Additional CSS classes for code input
 * - descriptionClassName: string - Additional CSS classes for description input
 * - disabled: boolean - Disable both inputs
 * - compareBranchCode: string - Optional branch code (e.g. 'C47') to show a stock
 *   comparison box for, right under our own stock figure in each result row
 * - compareBranchLabel: string - Optional display label for that branch (defaults to the code)
 * 
 * FEATURES:
 * ---------
 * - Debounced search (300ms)
 * - CTRL key toggle to show all stock vs in-stock only
 * - Red O.O.S badge for out-of-stock items
 * - Yellow stock-on-order indicator
 * - Keyboard navigation (Arrow keys + Enter)
 * - Click outside to close dropdown
 * - Search works from BOTH code and description inputs
 * 
 * REFERENCE NAME: ProductLookup
 * FILE LOCATION: /app/frontend/src/components/ProductLookup.jsx
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import { Input } from './ui/input';
import { Search, Package, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const enconOrange = '#ee7203';

const ProductLookup = ({
  codeValue = '',
  descriptionValue = '',
  onSelect,
  onCodeChange,
  onDescriptionChange,
  codePlaceholder = 'Code',
  descriptionPlaceholder = 'Product description',
  showPrice = false,  // GDPR: Prices hidden by default
  showStock = true,
  showWeight = false,
  codeClassName = 'w-32',
  descriptionClassName = 'flex-1',
  disabled = false,
  compareBranchCode = null,
  compareBranchLabel = null
}) => {
  const [codeInput, setCodeInput] = useState(codeValue);
  const [descriptionInput, setDescriptionInput] = useState(descriptionValue);
  const [searchResults, setSearchResults] = useState([]);
  const [allResults, setAllResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showInStockOnly, setShowInStockOnly] = useState(true);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [activeField, setActiveField] = useState(null); // 'code' or 'description'
  
  const wrapperRef = useRef(null);
  const codeInputRef = useRef(null);
  const descriptionInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Sync external value changes
  useEffect(() => {
    setCodeInput(codeValue);
  }, [codeValue]);

  useEffect(() => {
    setDescriptionInput(descriptionValue);
  }, [descriptionValue]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        setActiveField(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // CTRL key handler
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Control' && isOpen) {
        setShowInStockOnly(prev => !prev);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Filter results based on showInStockOnly
  useEffect(() => {
    if (showInStockOnly) {
      const filtered = allResults.filter(p => {
        const inStock = p.on_hand_quantity || p.on_hand_balance || p.free_stock || 0;
        const onOrder = p.on_order_quantity || 0;
        return inStock > 0 || onOrder > 0;
      });
      setSearchResults(filtered);
    } else {
      setSearchResults(allResults);
    }
  }, [showInStockOnly, allResults]);

  // Check if product is out of stock
  const isOutOfStock = (product) => {
    const inStock = product.on_hand_quantity || product.on_hand_balance || product.free_stock || 0;
    const onOrder = product.on_order_quantity || 0;
    return inStock <= 0 && onOrder <= 0;
  };

  // Search function - request more results to allow filtering
  const performSearch = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setAllResults([]);
      setSearchResults([]);
      setIsOpen(false);
      return;
    }

    setSearching(true);
    try {
      // Request more results (100) to allow frontend filtering for "show all" vs "in stock"
      const response = await api.get(`${API}/api/stock-database/search`, {
        params: { 
          q: query,
          limit: 100,
          include_all: true  // Signal to backend to include zero-stock items
        },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      const products = response.data.products || response.data.results || [];
      setAllResults(products);
      setIsOpen(products.length > 0);
      setHighlightedIndex(-1);
    } catch (error) {
      console.error('Product search error:', error);
      setAllResults([]);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  // Debounced search
  const debouncedSearch = useCallback((query) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      performSearch(query);
    }, 300);
  }, [performSearch]);

  // Handle code input change
  const handleCodeInputChange = (e) => {
    const value = e.target.value;
    setCodeInput(value);
    onCodeChange?.(value);
    setActiveField('code');
    debouncedSearch(value);
  };

  // Handle description input change
  const handleDescriptionInputChange = (e) => {
    const value = e.target.value;
    setDescriptionInput(value);
    onDescriptionChange?.(value);
    setActiveField('description');
    debouncedSearch(value);
  };

  // Handle product selection
  const handleSelect = (product) => {
    const code = product.product_code || product.code || '';
    const description = product.description || product.product_description || product.name || '';
    
    setCodeInput(code);
    setDescriptionInput(description);
    setIsOpen(false);
    setActiveField(null);
    
    onSelect?.(product);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen || searchResults.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < searchResults.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : searchResults.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < searchResults.length) {
          handleSelect(searchResults[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setActiveField(null);
        break;
      default:
        break;
    }
  };

  // Format currency
  const formatCurrency = (value) => {
    const num = parseFloat(value) || 0;
    return num.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Handle focus
  const handleCodeFocus = () => {
    setActiveField('code');
    if (codeInput && codeInput.length >= 2) {
      debouncedSearch(codeInput);
    }
  };

  const handleDescriptionFocus = () => {
    setActiveField('description');
    if (descriptionInput && descriptionInput.length >= 2) {
      debouncedSearch(descriptionInput);
    }
  };

  return (
    <div ref={wrapperRef} className="relative flex items-center gap-2 flex-1" data-testid="product-lookup-wrapper">
      {/* Product Code Input */}
      <div className={`relative ${codeClassName}`}>
        <Input
          ref={codeInputRef}
          value={codeInput}
          onChange={handleCodeInputChange}
          onFocus={handleCodeFocus}
          onKeyDown={handleKeyDown}
          placeholder={codePlaceholder}
          disabled={disabled}
          className="text-sm font-mono h-8"
          data-testid="product-lookup-code-input"
        />
      </div>

      {/* Description Input */}
      <div className={`relative ${descriptionClassName}`}>
        <Input
          ref={descriptionInputRef}
          value={descriptionInput}
          onChange={handleDescriptionInputChange}
          onFocus={handleDescriptionFocus}
          onKeyDown={handleKeyDown}
          placeholder={descriptionPlaceholder}
          disabled={disabled}
          className="text-sm h-8"
          data-testid="product-lookup-description-input"
        />
      </div>

      {/* Search Results Dropdown - Position relative to wrapper */}
      {isOpen && (
        <div 
          className="absolute z-50 left-0 right-0 top-full mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-xl overflow-hidden"
          data-testid="product-lookup-dropdown"
        >
          {/* Header with filter toggle */}
          <div className="px-3 py-2 bg-gray-900 border-b border-gray-700 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {searching ? (
                <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
              ) : (
                <Search className="h-4 w-4 text-gray-400" />
              )}
              <span className="text-xs text-gray-400">
                {searchResults.length} product{searchResults.length !== 1 ? 's' : ''}
                {showInStockOnly && allResults.length > searchResults.length && (
                  <span className="text-gray-500"> ({allResults.length} total)</span>
                )}
              </span>
            </div>
            <button
              onClick={() => setShowInStockOnly(!showInStockOnly)}
              className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                showInStockOnly 
                  ? 'bg-green-600 text-white' 
                  : 'bg-gray-700 text-gray-300'
              }`}
              data-testid="stock-filter-toggle"
            >
              {showInStockOnly ? 'IN STOCK' : 'ALL'}
            </button>
          </div>

          {/* CTRL hint */}
          <div className="px-3 py-1.5 bg-gray-800/50 border-b border-gray-700">
            <p className="text-[10px] text-gray-500">
              Press <kbd className="px-1 py-0.5 bg-gray-700 rounded text-gray-300 font-mono">CTRL</kbd> to {showInStockOnly ? 'show all stock' : 'show in-stock only'}
            </p>
          </div>

          {/* Results list */}
          <div className="overflow-y-auto max-h-[220px]">
            {searchResults.length === 0 ? (
              <div className="px-3 py-4 text-center text-gray-500 text-sm">
                {allResults.length > 0 
                  ? 'No in-stock products found. Press CTRL to show all.'
                  : 'No products found'
                }
              </div>
            ) : (
              searchResults.map((product, idx) => {
                const inStock = product.on_hand_quantity || product.on_hand_balance || product.free_stock || 0;
                const onOrder = product.on_order_quantity || 0;
                const outOfStock = isOutOfStock(product);
                
                return (
                  <div
                    key={product.product_code || idx}
                    onClick={() => handleSelect(product)}
                    className={`px-3 py-2 cursor-pointer transition-colors border-b border-gray-800 ${
                      highlightedIndex === idx 
                        ? 'bg-gray-700' 
                        : 'hover:bg-gray-800'
                    }`}
                    data-testid={`product-result-${idx}`}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Out of stock badge */}
                      {outOfStock && (
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-600 text-white">
                          O.O.S
                        </span>
                      )}
                      
                      {/* Product code */}
                      <span 
                        className="px-2 py-0.5 rounded text-xs font-bold text-white"
                        style={{ backgroundColor: enconOrange }}
                      >
                        {product.prefix || (product.product_code || '').substring(0, 3)}
                      </span>
                      <span className="font-semibold text-sm text-white">
                        {product.product_code || product.code}
                      </span>
                      
                      {/* Stock info */}
                      {showStock && (
                        <div className="ml-auto flex items-center gap-2">
                          <span className={`text-xs font-medium ${inStock > 0 ? 'text-green-400' : 'text-gray-500'}`}>
                            {inStock} {product.stock_unit || 'units'}
                          </span>
                          {onOrder > 0 && (
                            <span className="text-xs font-medium text-yellow-400">
                              +{onOrder} due
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Compare branch stock — the branch currently selected/viewed
                        (e.g. this transfer's source branch), shown right under our
                        own stock figure so the two can be compared at a glance. */}
                    {compareBranchCode && (() => {
                      const ob = (product.other_branches || []).find(b => b.branch_code === compareBranchCode);
                      const total = (ob?.on_hand ?? 0) - (ob?.due_out ?? 0);
                      return (
                        <div className="mt-1 flex justify-end" data-testid={`compare-branch-stock-${product.product_code}`}>
                          <div className="flex flex-col items-start gap-0.5 px-1.5 py-1 rounded bg-gray-100 border-2 border-purple-500">
                            <div className="flex items-center gap-1">
                              <span className="text-[10px] font-bold text-gray-700 underline">{compareBranchCode}</span>
                              <span className="text-[10px] text-gray-500">:</span>
                              <span className="text-[11px] font-bold text-orange-600">{total}</span>
                              <span className="text-[9px] text-gray-500">(OH {ob?.on_hand ?? 0})</span>
                            </div>
                            {((ob?.incoming || 0) > 0 || (ob?.due_out || 0) > 0) && (
                              <div className="flex items-center gap-2">
                                {(ob?.incoming || 0) > 0 && (
                                  <span className="text-[9px] font-semibold text-gray-700"><span className="text-emerald-600">In:</span> {ob.incoming}</span>
                                )}
                                {(ob?.due_out || 0) > 0 && (
                                  <span className="text-[9px] font-semibold text-gray-700"><span className="text-red-500">Out:</span> {ob.due_out}</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                    
                    {/* Description */}
                    <p className="text-xs text-gray-400 mt-1 truncate">
                      {product.description || product.product_description || product.name}
                    </p>
                    
                    {/* Price and weight */}
                    <div className="flex items-center gap-3 mt-1">
                      {showPrice && (product.unit_cost || product.unit_price || product.cost) && (
                        <span className="text-xs" style={{ color: enconOrange }}>
                          £{formatCurrency(product.unit_cost || product.unit_price || product.cost)}
                        </span>
                      )}
                      {showWeight && product.weight > 0 && (
                        <span className="text-xs text-purple-400">
                          {product.weight.toFixed(2)} kg
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductLookup;
